import type { FaceConfig } from "facesjs";
import { display } from "facesjs";
import { memo, useEffect, useRef, useState } from "react";
import { DEFAULT_JERSEY, DEFAULT_TEAM_COLORS } from "../../../../common/constants.ts";

// C2 (MOBILE_CONTENT_FIRST_PLAN_2026-07-06 阶段 C): reusable face thumbnail for
// entity rows and player cards, built on the same facesjs render path as the
// Player page (MyFace/PlayerPicture). Two perf red lines are enforced here so
// every caller gets them for free:
//
// 1. Lazy render — facesjs display() is expensive, so a thumb below the fold
//    renders only a fixed-size placeholder box and an IntersectionObserver;
//    the face is drawn the first time the row scrolls into view and then
//    stays rendered. `data-player-face-thumb="pending|rendered"` exposes the
//    state for audits/smoke tests.
// 2. Render-result cache — the SVG markup produced by display() is cached in
//    a bounded module-level map keyed by pid|jersey|colors, so re-mounts and
//    list re-renders never re-run the draw for a face already computed. The
//    component is additionally memoized.

const svgCache = new Map<string, string>();
const SVG_CACHE_LIMIT = 256;

const getFaceSvg = (
	cacheKey: string,
	face: FaceConfig,
	colors: [string, string, string],
	jersey: string,
) => {
	const cached = svgCache.get(cacheKey);
	if (cached !== undefined) {
		return cached;
	}

	const container = document.createElement("div");
	try {
		display(container, structuredClone(face), {
			teamColors: colors,
			jersey: {
				id: jersey,
			},
		});
	} catch {
		// Same posture as MyFace's ignoreDisplayErrors: a bad face config must
		// never break a list page.
		return undefined;
	}

	const svg = container.innerHTML;
	if (svgCache.size >= SVG_CACHE_LIMIT) {
		// Bounded FIFO eviction; iteration order of Map is insertion order.
		const oldestKey = svgCache.keys().next().value;
		if (oldestKey !== undefined) {
			svgCache.delete(oldestKey);
		}
	}
	svgCache.set(cacheKey, svg);
	return svg;
};

// Exported for tests.
export const getPlayerFaceThumbCacheKey = ({
	colors,
	jersey,
	pid,
}: {
	colors?: [string, string, string];
	jersey?: string;
	pid: number;
}) => `${pid}|${jersey ?? DEFAULT_JERSEY}|${(colors ?? DEFAULT_TEAM_COLORS).join(",")}`;

type PlayerFaceThumbProps = {
	className?: string;
	colors?: [string, string, string];
	face?: FaceConfig;
	imgURL?: string;
	jersey?: string;
	// Above-the-fold single cards may opt out of laziness; lists must not.
	lazy?: boolean;
	pid: number;
	size?: number;
};

export const PlayerFaceThumb = memo(
	({
		className,
		colors,
		face,
		imgURL,
		jersey,
		lazy = true,
		pid,
		size = 36,
	}: PlayerFaceThumbProps) => {
		const [visible, setVisible] = useState(!lazy);
		const ref = useRef<HTMLDivElement | null>(null);

		useEffect(() => {
			if (visible) {
				return;
			}
			const element = ref.current;
			if (!element || typeof IntersectionObserver === "undefined") {
				setVisible(true);
				return;
			}
			const observer = new IntersectionObserver(([entry]) => {
				if (entry?.isIntersecting) {
					setVisible(true);
				}
			});
			observer.observe(element);
			return () => {
				observer.disconnect();
			};
		}, [visible]);

		if (!face && !imgURL) {
			return null;
		}

		let content = null;
		if (visible) {
			if (imgURL) {
				content = (
					<img
						alt=""
						loading="lazy"
						src={imgURL}
						style={{
							height: "100%",
							objectFit: "cover",
							objectPosition: "top",
							width: "100%",
						}}
					/>
				);
			} else if (face) {
				const svg = getFaceSvg(
					getPlayerFaceThumbCacheKey({ colors, jersey, pid }),
					face,
					colors ?? DEFAULT_TEAM_COLORS,
					jersey ?? DEFAULT_JERSEY,
				);
				if (svg !== undefined) {
					content = (
						<div
							className="player-face-thumb-svg"
							// Trusted markup: facesjs output from our own face configs
							// (same trust level as SafeHtml's sanitized event text).
							dangerouslySetInnerHTML={{ __html: svg }}
							// Faces are 2:3 portraits; the square thumb crops to the head.
							style={{ height: size * 1.5, width: size }}
						/>
					);
				}
			}
		}

		return (
			<div
				aria-hidden="true"
				className={className ? `player-face-thumb ${className}` : "player-face-thumb"}
				data-player-face-thumb={visible ? "rendered" : "pending"}
				ref={ref}
				style={{ height: size, width: size }}
			>
				{content}
			</div>
		);
	},
);
